// App-wide locale (English / 简体中文) and milk-unit preference (oz / ml). Both
// persist to localStorage. useT() gives the current string catalog; useUnit()
// gives the unit + setter; useAmount() formats a stored oz value in the active
// unit + locale ("4 oz" / "120 ml" / "120 毫升").

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { catalogs, isLocale, type Locale, type Strings } from "./i18n";
import { setRuntimeLocale } from "./i18n/runtimeLocale";
import { isUnit, ozToUnitValue, unitValueToOz, type Unit } from "./units";

const STORAGE_KEY = "babyhelper:locale";
const UNIT_KEY = "babyhelper:unit";

function initialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  const locale = isLocale(stored)
    ? stored
    : navigator.language.toLowerCase().startsWith("zh")
      ? "zh"
      : "en";
  // Keep the non-React formatters (timeFmt, age) in sync from the first render.
  setRuntimeLocale(locale);
  return locale;
}

function initialUnit(): Unit {
  const stored = localStorage.getItem(UNIT_KEY);
  if (isUnit(stored)) return stored;
  // Default to whatever fits the caretaker's region — metric for Chinese, oz otherwise.
  return navigator.language.toLowerCase().startsWith("zh") ? "ml" : "oz";
}

type LocaleValue = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  unit: Unit;
  setUnit: (u: Unit) => void;
  t: Strings;
};

const LocaleCtx = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [unit, setUnitState] = useState<Unit>(initialUnit);

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  }, [locale]);

  function setLocale(l: Locale) {
    localStorage.setItem(STORAGE_KEY, l);
    setRuntimeLocale(l);
    setLocaleState(l);
  }

  function setUnit(u: Unit) {
    localStorage.setItem(UNIT_KEY, u);
    setUnitState(u);
  }

  return (
    <LocaleCtx.Provider value={{ locale, setLocale, unit, setUnit, t: catalogs[locale] }}>
      {children}
    </LocaleCtx.Provider>
  );
}

export function useLocale(): LocaleValue {
  const v = useContext(LocaleCtx);
  if (!v) throw new Error("useLocale must be used within LocaleProvider");
  return v;
}

export function useT(): Strings {
  return useLocale().t;
}

export function useUnit(): { unit: Unit; setUnit: (u: Unit) => void } {
  const { unit, setUnit } = useLocale();
  return { unit, setUnit };
}

/** Locale + unit-aware milk-amount formatting. All inputs are stored ounces. */
export function useAmount() {
  const { unit, t } = useLocale();
  const word = t.units[unit];
  return {
    unit,
    word,
    /** The number to show/enter in the active unit. */
    value: (oz: number) => ozToUnitValue(oz, unit),
    /** A value typed in the active unit, converted back to stored ounces. */
    toOz: (value: number) => unitValueToOz(value, unit),
    /** A full labelled amount, e.g. "4 oz" / "120 ml" / "120 毫升". */
    fmt: (oz: number) => `${ozToUnitValue(oz, unit)} ${word}`,
    /** A labelled range, e.g. "2–3 oz" / "60–90 ml". */
    range: (loOz: number, hiOz: number) => `${ozToUnitValue(loOz, unit)}–${ozToUnitValue(hiOz, unit)} ${word}`,
  };
}
